import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput, SelectPicker, TagPicker} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { getCitiesLookup } from '@/services/city.service';
import { RunningClub, UpsertRunningClub, ClubVisibilityEnum, ClubVisibilityList, ClubJoinTypeEnum, ClubJoinTypeSelectableList, ClubStatusEnum, ClubStatusList } from '@/models/running-club.model';
import {upsertRunningClub } from '@/services/running-club.service';
import Textarea from '@/components/Textarea';
import ColorListInput, { HEX_COLOR_REGEX } from '@/components/ColorListInput';
import { ClubTag, clubTagTypeLabel } from '@/models/club-tag.model';
import { getAllClubTags } from '@/services/club-tag.service';
import { toastErrorMessage } from '@/utils/toasterMessage';

const runningClubSchema = Schema.Model<UpsertRunningClub>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType(),
  logoUrl: Schema.Types.StringType(),
  brandColors: Schema.Types.ArrayType()
    .maxLength(8, 'At most 8 colors.')
    .of(Schema.Types.StringType().pattern(HEX_COLOR_REGEX, 'Use #RRGGBB or #RRGGBBAA.')),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  priority: Schema.Types.NumberType(),
  visibility: Schema.Types.NumberType().isRequired('This field is required.'),
  joinType: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  tagIds: Schema.Types.ArrayType(),
});

// Every enum field carries a real default. The API rejects an omitted enum with a
// 400 — [EnumDataType] fails the 0 that a missing non-nullable enum binds to — so
// leaving any of these undefined makes the form unsaveable.
const emptyModel: UpsertRunningClub = {
  id: undefined,
  name: '',
  description: '',
  priority: undefined,
  cityId: undefined,
  logoUrl: undefined,
  brandColors: undefined,
  visibility: ClubVisibilityEnum.Public,
  joinType: ClubJoinTypeEnum.NotOwned,
  status: ClubStatusEnum.Draft,
  tagIds: [],
};

interface RunningClubFormProps{
  formObject?: RunningClub;
  pageState: PageState
};

const RunningClubForm: React.FC<RunningClubFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertRunningClub>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [allTags, setAllTags] = useState<ClubTag[]>([]);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    getAllClubTags()
      .then(setAllTags)
      .catch(() => toastErrorMessage('Error in fetching club tags'));
  }, []);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      // The API returns tag objects but takes tag ids on upsert.
      const { tags, ...rest } = formObject;
      setFormValue({ ...rest, tagIds: (tags ?? []).map(t => t.id) });
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunningClub> = await upsertRunningClub(formValue);
      if (result.success) {
        if(pageState == PageState.New){
          nav(`/running-clubs/${result.result.id}`, {state:{tab: 'running-club-links'}});
        } else {
          nav(`/running-clubs`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  // The API refuses Auto/Manual on an ownerless club and refuses NotOwned on an
  // owned one. JoinType is the only readable signal for ownership on this object,
  // so derive from it rather than fetching the member list just for this.
  const hasOwner = formValue.joinType !== ClubJoinTypeEnum.NotOwned;

  return (
    <PageContent>
      <Form name='run-club-form' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={runningClubSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:4}} style={{textAlign: 'center'}}>
              <Form.Label>Logo</Form.Label>              
              <Form.Control
                accepter={ImageUploader}
                name="logoUrl"
                height="200px"
                width="200px"
                url={formValue.logoUrl}
                objectStorageCategory={ObjectStorageFileCategory.RunningClub}
                onChange={url => setFormValue(pre => ({...pre, logoUrl: url}))}                
              />
            </Col> 
            <Col span={{xs:24, xl:20}} >
              <Row>
                <Col span={{xs:24, lg:8}}>
                  <Form.Group>
                    <Form.Label>Name </Form.Label>
                    <Form.Control name="name"/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:8}} >
                  <Form.Group>
                    <Form.Label>City</Form.Label>
                    <Form.Control name="cityId" accepter={FetchingSelect} block
                      fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
                      placeholder="Select a city"
                    />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:8}} >
                  <Form.Group>
                    <Form.Label>Priority</Form.Label>
                    <Form.Control name="priority" accepter={NumberInput}
                      onChange={value => setFormValue(pre => ({...pre, priority: value || undefined}))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, lg:8}} >
                  <Form.Group>
                    <Form.Label>Visibility</Form.Label>
                    <Form.Control name="visibility" accepter={SelectPicker} block
                      data={ClubVisibilityList}
                      searchable={false}
                      cleanable={false}
                    />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:8}} >
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Control name="status" accepter={SelectPicker} block
                      data={ClubStatusList}
                      searchable={false}
                      cleanable={false}
                    />
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:8}} >
                  <Form.Group>
                    <Form.Label>Join type</Form.Label>
                    <Form.Control name="joinType" accepter={SelectPicker} block
                      data={ClubJoinTypeSelectableList}
                      searchable={false}
                      cleanable={false}
                      disabled={!hasOwner}
                    />
                    {!hasOwner && (
                      <Form.HelpText>Assign an owner first — an ownerless club cannot accept members.</Form.HelpText>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={4} /> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Tags</Form.Label>
                    <Form.Control name="tagIds" accepter={TagPicker} block
                      data={allTags.map(t => ({ value: t.id, label: t.title, type: clubTagTypeLabel(t.type) }))}
                      groupBy="type"
                      placeholder="Select tags"
                      onChange={ids => setFormValue(pre => ({...pre, tagIds: ids ?? []}))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Brand colors</Form.Label>
                    <Form.Control name="brandColors" accepter={ColorListInput}
                      onChange={colors => setFormValue(pre => ({...pre, brandColors: colors}))}
                    />
                    <Form.HelpText>
                      Order matters — the first two drive the captain profile picture gradient in the app.
                      Leave empty to fall back to the Elites gradient.
                    </Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
                   
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default RunningClubForm;

